const centralDomain = () =>
  (process.env.VUE_APP_CENTRAL_DOMAIN || "donequickhr.com").toLowerCase();

const tenantDomain = () =>
  (process.env.VUE_APP_TENANT_DOMAIN || centralDomain()).toLowerCase();

const adminDomain = () =>
  (process.env.VUE_APP_ADMIN_DOMAIN || "admin.donequickhr.com").toLowerCase();

export { adminDomain };

const localCentralHostnames = new Set(["127.0.0.1", "localhost"]);

export function currentHostname() {
  return window.location.hostname.toLowerCase();
}

export function isCentralHost() {
  const host = currentHostname();
  const central = centralDomain();

  if (host === central || host === `www.${central}`) {
    return true;
  }

  // Local dev: marketing may be opened via 127.0.0.1 or Laragon .test hostnames.
  if (
    localCentralHostnames.has(host) &&
    (localCentralHostnames.has(central) ||
      central === "127.0.0.1" ||
      central === "localhost")
  ) {
    return true;
  }

  return false;
}

export function isAdminHost() {
  return currentHostname() === adminDomain();
}

export function isTenantHost() {
  return !isCentralHost() && !isAdminHost();
}

export function tenantSlugFromHost() {
  if (!isTenantHost()) return null;
  const host = currentHostname();
  const base = tenantDomain();
  if (!host.endsWith(`.${base}`)) return null;
  return host.slice(0, -(base.length + 1));
}

export function isTenancyFrontendEnabled() {
  return String(process.env.VUE_APP_TENANCY_ENABLED || "false").toLowerCase() === "true";
}

export function shouldSendCompanyHeader() {
  return !isTenancyFrontendEnabled() || !isTenantHost();
}

export function slugifyWorkspaceName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function tenantDomainLabel() {
  return tenantDomain();
}

export function workspacePreviewUrl(slug) {
  const normalized = slugifyWorkspaceName(slug);
  if (!normalized) return "";

  const base = tenantDomain();
  const scheme = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";

  return `${scheme}//${normalized}.${base}${port}`;
}

/** Central marketing site URL (same port as current page for local dev). */
export function centralMarketingUrl(path = "/") {
  const scheme = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${scheme}//${centralDomain()}${port}${normalizedPath}`;
}

/** Build a full tenant- or central-aware API URL for a relative path. */
export function apiUrl(path = "") {
  const base = resolveApiBaseUrl();
  const normalizedPath = String(path).replace(/^\/+/, "");
  return `${base}${normalizedPath}`;
}

/** API base URL — tenant host uses its subdomain; super admin uses admin API host. */
export function resolveApiBaseUrl() {
  const configured = process.env.VUE_APP_API_URL || "http://127.0.0.1:8000/api/";
  const normalized = configured.endsWith("/") ? configured : `${configured}/`;

  if (isTenancyFrontendEnabled() && isAdminHost()) {
    try {
      const url = new URL(configured);
      const port = url.port ? `:${url.port}` : "";
      return `${url.protocol}//${adminDomain()}${port}/api/`;
    } catch {
      return normalized;
    }
  }

  if (!isTenancyFrontendEnabled() || !isTenantHost()) {
    return normalized;
  }

  const slug = tenantSlugFromHost();
  if (!slug) {
    return normalized;
  }

  try {
    const url = new URL(configured);
    const port = url.port ? `:${url.port}` : "";
    return `${url.protocol}//${slug}.${tenantDomain()}${port}/api/`;
  } catch {
    return normalized;
  }
}

function resolveRegistrationRedirectUrl(data) {
  const domain = String(data?.domain || "").trim();
  let target = String(data?.redirect_url || "").trim();

  if (!target && !domain) {
    return null;
  }

  if (!/^https?:\/\//i.test(target)) {
    const port = window.location.port ? `:${window.location.port}` : "";
    const scheme = window.location.protocol;
    const path = target.startsWith("onboarding=") ? "/auth/verify-email" : "/";
    const query =
      target.startsWith("?") || target.startsWith("onboarding=")
        ? target.startsWith("?")
          ? target
          : `?${target}`
        : "";
    target = `${scheme}//${domain}${port}${path}${query}`;
  }

  return target;
}

function navigateViaAnchor(url) {
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Navigate after tenancy signup.
 * Avoid slug.127.0.0.1 URLs locally — browsers reject them as invalid URLs.
 */
export function navigateToRegistrationRedirect(data) {
  const target = resolveRegistrationRedirectUrl(data);
  if (!target) {
    return false;
  }

  try {
    window.location.assign(target);
    return true;
  } catch (_) {
    navigateViaAnchor(target);
    return true;
  }
}
